import { Link } from '@remix-run/react';
import { cn } from '@theprintbar/shared';
import React, { ComponentProps, forwardRef } from 'react';
import { ButtonSize, ButtonVariant, defaultButtonClasses, getButtonIconSize } from '.';
import { CustomSVG } from '../../../src';
import { Spinner } from '../Spinner';

interface LinkButtonProps extends ComponentProps<typeof Link> {
	icon?: CustomSVG;
	iconClass?: string;
	circle?: boolean;
	spinner?: boolean;
	variant?: ButtonVariant;
	size?: ButtonSize;
	iconSize?: ButtonSize;
	disabled?: boolean;
}

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
	(
		{
			children,
			variant = ButtonVariant.primary,
			size = ButtonSize.M,
			iconSize,
			spinner,
			icon: Icon,
			iconClass,
			circle,
			className,
			disabled,
			...props
		},
		ref,
	) => (
		<Link
			ref={ref}
			className={cn(
				defaultButtonClasses,
				variant,
				circle ? 'p-2' : size,
				className,
				disabled && 'text-grey opacity-50 pointer-events-none transition-all duration-500',
			)}
			prefetch="intent"
			aria-disabled={disabled}
			{...props}
		>
			{spinner ? <Spinner className={getButtonIconSize(iconSize || size)} /> : children}
			{Icon && (
				<Icon
					className={cn(
						spinner && 'opacity-0',
						!circle && (size !== ButtonSize.L ? 'ml-1' : 'ml-2'),
						getButtonIconSize(iconSize || size),
						iconClass,
					)}
					aria-hidden="true"
				/>
			)}
		</Link>
	),
);

export const PrimaryLinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>((props, ref) => (
	<LinkButton {...props} ref={ref} variant={ButtonVariant.primary} />
));
export const OutlineLinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>((props, ref) => (
	<LinkButton {...props} ref={ref} variant={ButtonVariant.outline} />
));

export const TransparentLinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>((props, ref) => (
	<LinkButton {...props} ref={ref} variant={ButtonVariant.transparent} />
));

export const ColouredLinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>((props, ref) => (
	<LinkButton {...props} ref={ref} variant={ButtonVariant.coloured} />
));
